import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Search, Filter, Archive } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { cn } from '../../../../../lib/utils';

function FiltersPanel({
  growLevels,
  selectedLevels,
  setSelectedLevels,
  archived,
  setArchived,
  onSearchChange,
  isDisabled,
  setIsDisabled,
}) {
  const [localSearchValue, setLocalSearchValue] = useState('');

  const handleLevelToggle = (levelId) => {
    setIsDisabled(true);
    if (selectedLevels.includes(levelId)) {
      setSelectedLevels(selectedLevels.filter((id) => id !== levelId));
    } else {
      setSelectedLevels([...selectedLevels, levelId]);
    }
  };

  const handleArchivedToggle = () => {
    setIsDisabled(true);
    setArchived(!archived);
  };

  const handleSearchInputChange = (e) => {
    const { value } = e.target;
    setLocalSearchValue(value);
    onSearchChange(value);
  };

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative p-0.5">
        <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search by email..."
          value={localSearchValue}
          onChange={handleSearchInputChange}
          className={cn(
            'w-full pl-9 pr-4 py-2 rounded-lg text-sm',
            'bg-background border border-input',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
            'placeholder:text-muted-foreground',
          )}
        />
      </div>

      {/* Level Filters */}
      <Card style={{ border: '1px solid hsl(var(--border))' }}>
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filter by Level
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="flex flex-wrap gap-2">
            {growLevels.map((level) => {
              const isSelected = selectedLevels.includes(level._id);
              return (
                <button
                  key={level._id}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => handleLevelToggle(level._id)}
                  className={cn(
                    'px-3 py-1.5 rounded-full text-xs font-medium transition-all',
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80',
                    isDisabled && 'opacity-50 cursor-not-allowed',
                  )}
                >
                  {level.key}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Archived Filter */}
      <Card style={{ border: '1px solid hsl(var(--border))' }}>
        <CardContent className="p-4">
          <button
            type="button"
            disabled={isDisabled}
            onClick={handleArchivedToggle}
            className={cn(
              'w-full flex items-center gap-3 p-2 rounded-lg transition-all',
              archived ? 'bg-primary/10' : 'hover:bg-muted',
              isDisabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            <div
              className={cn(
                'p-2 rounded-lg',
                archived ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground',
              )}
            >
              <Archive className="h-4 w-4" />
            </div>
            <div className="text-left">
              <p className={cn('text-sm font-medium', archived && 'text-primary')}>Show Archived</p>
              <p className="text-xs text-muted-foreground">
                {archived ? 'Showing archived users' : 'Hidden archived users'}
              </p>
            </div>
          </button>
        </CardContent>
      </Card>
    </div>
  );
}

FiltersPanel.propTypes = {
  growLevels: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      key: PropTypes.string.isRequired,
      _id: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selectedLevels: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedLevels: PropTypes.func.isRequired,
  archived: PropTypes.bool.isRequired,
  setArchived: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  setIsDisabled: PropTypes.func.isRequired,
};

export default FiltersPanel;
